/** The tally, the ledger, and redeeming. */
import { useCallback, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Loading } from "../components/Loading";
import { PayoutDetailsForm } from "../components/PayoutDetailsForm";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { useToast } from "../hooks/useToast";
import {
  earnings as earningsApi,
  payouts as payoutsApi,
} from "../api/endpoints";
import { formatDate } from "../lib/validation";
import { useAuth } from "../store/auth";
import type {
  EarningsSummary,
  LedgerEntry,
  Payout,
  PayoutDetails,
  PayoutEligibility,
} from "../types/api";

const PAYOUT_LABELS: Record<string, string> = {
  pending: "Requested",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
  cancelled: "Cancelled",
};

/**
 * The money is there and we have nowhere to send it.
 *
 * Shown instead of the redeem button rather than beside it: a disabled
 * button with an explanation underneath makes people hunt for what to do.
 */
function PayoutNudge({
  missing,
  editing,
  existing,
  country,
  onOpen,
  onClose,
  onSaved,
}: {
  missing: string[];
  editing: boolean;
  existing?: PayoutDetails | null;
  country?: string | null;
  onOpen: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  if (editing) {
    return (
      <div className="mt-16">
        <PayoutDetailsForm
          existing={existing}
          defaultCountry={country}
          submitLabel="Save and redeem"
          onSaved={onSaved}
          secondary={
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="payout-nudge mt-16">
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        We need your bank details before we can send this
      </div>
      <p className="muted" style={{ margin: "0 0 12px", fontSize: 13 }}>
        {missing.length > 0
          ? `Still needed: ${missing.join(", ").toLowerCase()}.`
          : "It takes a minute, and you only do it once."}
      </p>
      <button type="button" className="btn btn-primary" onClick={onOpen}>
        Add your bank details →
      </button>
    </div>
  );
}

export function Earnings() {
  const toast = useToast();
  // Their account country, as the default for where they bank.
  const country = useAuth((state) => state.contributor?.country);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [eligibility, setEligibility] = useState<PayoutEligibility | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [payoutHistory, setPayoutHistory] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  // What they want to take out. Pre-filled with the whole balance, because
  // that is what most people want — but theirs to change.
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The prompt that catches everybody who joined before bank details
  // existed, and everybody who skipped the step on the way in.
  const [editingDetails, setEditingDetails] = useState(false);
  const [activityTab, setActivityTab] = useState<"all" | "earnings" | "payouts">("all");

  // Bumping this re-runs the loader. Keeping the fetch inside the effect
  // (rather than calling a setState-ing helper from it) means the state
  // updates happen after the await and are skipped once cancelled, so
  // navigating away mid-load never writes to an unmounted screen.
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [summaryResult, eligibilityResult, ledgerResult, payoutResult] =
          await Promise.all([
            earningsApi.summary(),
            payoutsApi.eligibility(),
            earningsApi.ledger(),
            payoutsApi.mine(),
          ]);
        if (cancelled) return;
        setSummary(summaryResult);
        setEligibility(eligibilityResult);
        setLedger(ledgerResult.entries);
        setPayoutHistory(payoutResult.payouts);
        setError(null);
        // Only pre-fill while they have not typed anything, so a reload does
        // not wipe an amount they were part-way through entering.
        if (!amountTouched) {
          setAmount((summaryResult.balance.cents / 100).toFixed(2));
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  // An approval elsewhere moves the balance, so pick it up on return.
  // Never while an amount is part-typed, or it would be overwritten.
  useRefreshOnFocus(reload, { enabled: !redeeming && !amountTouched });

  async function handleRedeem() {
    if (amountError) return;
    setRedeeming(true);
    try {
      const payout = await payoutsApi.redeem({ amount_cents: amountCents });
      toast.show(`Payout of $${payout.amount.formatted} requested.`, "success");
      setAmountTouched(false);
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setRedeeming(false);
    }
  }

  async function handleCancel(payoutId: string) {
    try {
      await payoutsApi.cancel(payoutId);
      toast.show("Payout cancelled — the balance is back in your account.", "success");
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    }
  }

  // Parsed once and validated against the same rules the API applies, so the
  // person is told before the round trip rather than after it.
  const amountCents = Math.round(parseFloat(amount.replace(/[^0-9.]/g, "")) * 100);
  const amountError = ((): string | null => {
    if (!summary || !eligibility) return null;
    if (!amount.trim() || Number.isNaN(amountCents)) return "Enter an amount";
    if (amountCents < eligibility.minimum.cents)
      return `The smallest payout is $${eligibility.minimum.formatted}`;
    if (amountCents > summary.balance.cents)
      return `That's more than your $${summary.balance.formatted} balance`;
    return null;
  })();

  if (loading) return <Layout title="Earnings"><Loading label="Adding it up…" /></Layout>;
  if (error) {
    return (
      <Layout title="Earnings">
        <section className="section">
          <div className="wrap narrow">
            <div className="panel">
              <div className="form-error">{error}</div>
              <button type="button" className="btn btn-ghost btn-sm mt-16" onClick={reload}>
                Try again
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }
  if (!summary || !eligibility) return null;

  const bank = eligibility.payout_details?.details ?? null;
  // The only thing standing between them and their money, told apart from
  // every other reason they might not be able to redeem yet.
  const needsDetails =
    eligibility.payout_method !== "paypal" &&
    eligibility.payout_details?.complete === false;

  const progressToThreshold = Math.min(
    100,
    (summary.balance.cents / Math.max(1, summary.payout_threshold.cents)) * 100,
  );
  const reached = eligibility.can_redeem;
  const progressLabel = reached
    ? "You've reached the minimum payout."
    : summary.amount_to_threshold.cents > 0
      ? `You need $${summary.amount_to_threshold.formatted} more before you can redeem your earnings.`
      : eligibility.reason || "You can't redeem yet.";
  const redeemLabel = reached && !amountError
    ? `Redeem $${(amountCents / 100).toFixed(2)}`
    : eligibility.payout_method === "paypal"
      ? "Redeem to PayPal"
      : "Redeem";
  const destination = eligibility.paypal_email
    ? `Payments are sent to PayPal account: ${eligibility.paypal_email}`
    : bank
      ? `Payments are sent to ${bank.bank_name} ····${bank.account_number_last4}`
      : "";

  const payoutById = new Map(payoutHistory.map((payout) => [payout.payout_id, payout]));
  const seenPayouts = new Set(ledger.map((entry) => entry.payout_id).filter(Boolean));
  const activity = [
    ...ledger.map((entry) => {
      const payout = entry.payout_id ? payoutById.get(entry.payout_id) : undefined;
      return {
        id: entry.entry_id,
        kind: entry.amount.cents < 0 ? "payout" as const : "earning" as const,
        title: entry.reason,
        meta: formatDate(entry.created_at),
        cents: entry.amount.cents,
        cancelId: payout?.status === "pending" ? payout.payout_id : null,
      };
    }),
    ...payoutHistory
      .filter((payout) => !seenPayouts.has(payout.payout_id))
      .map((payout) => ({
        id: payout.payout_id,
        kind: "payout" as const,
        title: payout.method === "paypal" ? "PayPal payout" : "PayPal/Wise payout",
        meta: `${formatDate(payout.requested_at)} · ${PAYOUT_LABELS[payout.status] ?? payout.status}`,
        cents: -Math.abs(payout.amount.cents),
        cancelId: payout.status === "pending" ? payout.payout_id : null,
      })),
  ].filter((entry) =>
    activityTab === "all" || (activityTab === "earnings" ? entry.kind === "earning" : entry.kind === "payout"),
  );

  return (
    <Layout title="Earnings">
      <section className="page earn-page">
        <div>
          <div className="earn-head">
            <div>
              <div className="page-kicker">EARNINGS</div>
              <h1>Your earnings</h1>
            </div>
            {!needsDetails && (
              <button type="button" className="btn btn-primary earn-account" onClick={() => setEditingDetails(true)}>
                Account details
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>

          <div className={`earn-grid ${reached ? "is-ready" : ""}`}>
            <div className="earn-available">
              <div className="earn-kicker">Available to redeem</div>
              <div className="earn-amount">
                ${summary.balance.formatted}
                <span>USD</span>
              </div>
              <p className="earn-mix">
                {summary.recordings_approved} approved · {summary.recordings_pending} in review
              </p>
              <div className="earn-progress-meta">
                <span>${summary.payout_threshold.formatted} minimum payout</span>
                <span className={reached ? "is-reached" : ""}>
                  {reached ? "Reached" : `${Math.round(progressToThreshold)}%`}
                </span>
              </div>
              <div className="earn-track">
                <i className={reached ? "ready" : "partial"} style={{ width: `${progressToThreshold}%` }} />
              </div>
              <p className="earn-note">{progressLabel}</p>

              {reached && (
                <div className={amountTouched && amountError ? "field err earn-amount-field" : "field earn-amount-field"}>
                  <label htmlFor="redeem-amount">How much would you like to take out?</label>
                  <div className="amount-input">
                    <span className="amount-prefix">$</span>
                    <input
                      id="redeem-amount"
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => {
                        setAmount(event.target.value);
                        setAmountTouched(true);
                      }}
                      onBlur={() => setAmountTouched(true)}
                    />
                  </div>
                  <div className="err-msg">{amountError}</div>
                </div>
              )}

              {needsDetails ? (
                <PayoutNudge
                  missing={eligibility.payout_details?.missing ?? []}
                  editing={editingDetails}
                  onOpen={() => setEditingDetails(true)}
                  onClose={() => setEditingDetails(false)}
                  existing={eligibility.payout_details?.details}
                  country={country}
                  onSaved={() => {
                    setEditingDetails(false);
                    toast.show("Payout details saved. You can redeem now.", "success");
                    reload();
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="btn btn-primary earn-redeem"
                  disabled={!reached || redeeming || amountError !== null}
                  onClick={() => void handleRedeem()}
                >
                  {redeeming ? "Requesting…" : redeemLabel}
                  {reached && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              )}

              {destination && <p className="earn-destination">{destination}</p>}

              {editingDetails && !needsDetails && (
                <div className="mt-16">
                  <PayoutDetailsForm
                    existing={bank}
                    defaultCountry={bank?.country ?? country}
                    submitLabel="Save payout details"
                    onSaved={() => {
                      setEditingDetails(false);
                      toast.show("Payout details updated.", "success");
                      reload();
                    }}
                    secondary={
                      <button type="button" className="btn btn-ghost" onClick={() => setEditingDetails(false)}>
                        Cancel
                      </button>
                    }
                  />
                </div>
              )}
            </div>

            <div className="earn-side">
              <div className="earn-stat">
                <span>In review</span>
                <strong>${summary.pending_value.formatted}</strong>
                <p>Value of voice notes currently being reviewed.</p>
              </div>
              <div className="earn-stat earn-lifetime">
                <span>Lifetime earnings</span>
                <strong>${summary.lifetime_earned.formatted}</strong>
                <p>Total approved earnings</p>
              </div>
              <div className="earn-stat">
                <span>Paid out</span>
                <strong>${summary.paid_out.formatted}</strong>
                <p>
                  {eligibility.payout_method === "paypal"
                    ? "Total already sent through PayPal."
                    : "Total already sent."}
                </p>
              </div>
            </div>
          </div>

          {summary.reserved.cents > 0 && (
            <div className="hint mt-8">
              ${summary.reserved.formatted} is being processed and is not available to redeem.
            </div>
          )}

          <div className="earn-activity">
            <div className="earn-activity-head">
              <h2>Activity</h2>
              <div className="earn-tabs" role="tablist">
                {(["all", "earnings", "payouts"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={activityTab === tab ? "on" : ""}
                    onClick={() => setActivityTab(tab)}
                  >
                    {tab === "all" ? "All" : tab === "earnings" ? "Earnings" : "Payouts"}
                  </button>
                ))}
              </div>
            </div>
            {activity.length === 0 ? (
              <p className="earn-empty">Nothing in this list yet.</p>
            ) : (
              activity.map((entry) => (
                <div key={entry.id} className="earn-row">
                  <i className={entry.kind} aria-hidden="true" />
                  <div>
                    <strong>{entry.title}</strong>
                    <span>{entry.meta}</span>
                  </div>
                  <b className={entry.cents >= 0 ? "plus" : "minus"}>
                    {entry.cents >= 0 ? "+" : "−"}${Math.abs(entry.cents / 100).toFixed(2)}
                  </b>
                  {entry.cancelId && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void handleCancel(entry.cancelId!)}>
                      Cancel
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
